import { useState, useEffect } from 'react';
import { Card } from '@/components/ui/card';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, BarChart, Bar } from 'recharts';
import { TrendingUp, TrendingDown, Activity, DollarSign } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AnalyticsTab = () => {
  const [analytics, setAnalytics] = useState(null);
  const [trades, setTrades] = useState([]);

  useEffect(() => {
    fetchAnalytics();
    fetchTrades();
  }, []);

  const fetchAnalytics = async () => {
    try {
      const response = await axios.get(`${API}/analytics`, { withCredentials: true });
      setAnalytics(response.data);
    } catch (error) {
      console.error('Failed to fetch analytics:', error);
    }
  };

  const fetchTrades = async () => {
    try {
      const response = await axios.get(`${API}/trades`, { withCredentials: true });
      setTrades(response.data);
    } catch (error) {
      console.error('Failed to fetch trades:', error);
    }
  };

  if (!analytics) {
    return <Card className="p-8">Loading...</Card>;
  }

  return (
    <div className="space-y-6" data-testid="analytics-tab-content">
      {/* Stats Cards */}
      <div className="grid md:grid-cols-4 gap-4">
        <StatCard
          icon={<DollarSign className="w-6 h-6" />}
          label="Total P&L"
          value={`₹${analytics.total_pnl.toFixed(2)}`}
          trend={analytics.total_pnl >= 0 ? 'up' : 'down'}
          color="indigo"
        />
        <StatCard
          icon={<Activity className="w-6 h-6" />}
          label="Win Rate"
          value={`${analytics.win_rate.toFixed(1)}%`}
          trend="up"
          color="green"
        />
        <StatCard
          icon={<TrendingUp className="w-6 h-6" />}
          label="Total Trades"
          value={analytics.total_trades}
          color="blue"
        />
        <StatCard
          icon={<TrendingUp className="w-6 h-6" />}
          label="ROI"
          value={`${analytics.roi.toFixed(1)}%`}
          trend={analytics.roi >= 0 ? 'up' : 'down'}
          color="purple"
        />
      </div>

      {/* P&L Chart */}
      <Card className="p-6 bg-white/80 backdrop-blur-sm border-gray-200">
        <h3 className="text-lg font-semibold text-gray-900 mb-4" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
          P&L Over Time
        </h3>
        <ResponsiveContainer width="100%" height={300}>
          <LineChart data={analytics.pnl_data}>
            <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
            <XAxis dataKey="date" stroke="#6b7280" fontSize={12} />
            <YAxis stroke="#6b7280" fontSize={12} />
            <Tooltip
              contentStyle={{ backgroundColor: 'white', border: '1px solid #e5e7eb', borderRadius: '8px' }}
            />
            <Line type="monotone" dataKey="pnl" stroke="#6366f1" strokeWidth={2} dot={false} />
          </LineChart>
        </ResponsiveContainer>
      </Card>

      {/* Recent Trades */}
      <Card className="p-6 bg-white/80 backdrop-blur-sm border-gray-200">
        <h3 className="text-lg font-semibold text-gray-900 mb-4" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
          Recent Trades
        </h3>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="border-b border-gray-200">
              <tr>
                <th className="text-left py-3 px-4 text-sm font-semibold text-gray-700">Time</th>
                <th className="text-left py-3 px-4 text-sm font-semibold text-gray-700">Symbol</th>
                <th className="text-left py-3 px-4 text-sm font-semibold text-gray-700">Action</th>
                <th className="text-right py-3 px-4 text-sm font-semibold text-gray-700">Qty</th>
                <th className="text-right py-3 px-4 text-sm font-semibold text-gray-700">Price</th>
                <th className="text-right py-3 px-4 text-sm font-semibold text-gray-700">P&L</th>
              </tr>
            </thead>
            <tbody>
              {trades.slice(0, 10).map((trade, idx) => (
                <tr key={idx} className="border-b border-gray-100 hover:bg-slate-50">
                  <td className="py-3 px-4 text-sm text-gray-600">
                    {new Date(trade.timestamp).toLocaleTimeString()}
                  </td>
                  <td className="py-3 px-4 text-sm text-gray-900 font-medium">{trade.symbol}</td>
                  <td className="py-3 px-4">
                    <span className={`px-2 py-1 text-xs rounded-full ${
                      trade.action === 'BUY' ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'
                    }`}>
                      {trade.action}
                    </span>
                  </td>
                  <td className="py-3 px-4 text-sm text-gray-900 text-right">{trade.quantity}</td>
                  <td className="py-3 px-4 text-sm text-gray-900 text-right">₹{trade.price.toFixed(2)}</td>
                  <td className={`py-3 px-4 text-sm font-semibold text-right ${
                    trade.pnl >= 0 ? 'text-green-600' : 'text-red-600'
                  }`}>
                    ₹{trade.pnl.toFixed(2)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>
    </div>
  );
};

const StatCard = ({ icon, label, value, trend, color }) => {
  const colorClasses = {
    indigo: 'bg-indigo-100 text-indigo-700',
    green: 'bg-green-100 text-green-700',
    blue: 'bg-blue-100 text-blue-700',
    purple: 'bg-purple-100 text-purple-700'
  };

  return (
    <Card className="p-6 bg-white/80 backdrop-blur-sm border-gray-200">
      <div className="flex items-start justify-between mb-3">
        <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${colorClasses[color]}`}>
          {icon}
        </div>
        {trend && (
          trend === 'up' ? <TrendingUp className="w-5 h-5 text-green-600" /> : <TrendingDown className="w-5 h-5 text-red-600" />
        )}
      </div>
      <p className="text-sm text-gray-600 mb-1">{label}</p>
      <p className="text-2xl font-bold text-gray-900">{value}</p>
    </Card>
  );
};

export default AnalyticsTab;
